import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Upload, MapPin, Plus, X, Camera } from 'lucide-react';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { uploadFile } from '../lib/storage';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import HighlightsManager from '../components/HighlightsManager';
import { difficulties } from '../data/mock';
import tagLabel, { TAG_GROUPS } from '../utils/tagLabels';
import { useToast } from '../hooks/use-toast';

const SUBMIT_CATEGORIES = [
  { label: 'Nature & Parks', slug: 'nature-parks' },
  { label: 'Hidden Gems', slug: 'hidden-gems' },
  { label: 'Architecture', slug: 'architecture' },
  { label: 'Food-Focused Walks', slug: 'food-focused-walks' },
  { label: 'Scenic Walks', slug: 'scenic-walks' },
  { label: 'Historic Walks', slug: 'historic-walks' },
  { label: 'Family-Friendly', slug: 'family-friendly' },
  { label: 'Sunset Walks', slug: 'sunset-walks' },
];

const MAPS_LIBRARIES = ['places'];

const SubmitWalkPage = () => {
  const { toast } = useToast();
  const { isAuthenticated } = useAuth();

  const { isLoaded: mapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: MAPS_LIBRARIES,
  });

  const startingAutocompleteRef = useRef(null);
  const endingAutocompleteRef = useRef(null);
  const [startingCoords, setStartingCoords] = useState(null);
  const [endingCoords, setEndingCoords] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Auto-calculate walking distance when both addresses are selected
  useEffect(() => {
    if (!startingCoords || !endingCoords || !mapsLoaded) return;
    const svc = new window.google.maps.DirectionsService();
    svc.route(
      {
        origin: startingCoords,
        destination: endingCoords,
        travelMode: window.google.maps.TravelMode.WALKING,
      },
      (result, status) => {
        if (status === 'OK') {
          const leg = result.routes[0].legs[0];
          const miles = (leg.distance.value / 1609.34).toFixed(1);
          handleInputChange('distance', `${miles} miles`);
          if (!formData.duration) {
            handleInputChange('duration', leg.duration.text);
          }
        }
      }
    );
  }, [startingCoords, endingCoords, mapsLoaded]);

  const handlePlaceSelect = (autocompleteRef, field, setCoords) => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;
    handleInputChange(field, place.formatted_address);
    setCoords({ lat: place.geometry.location.lat(), lng: place.geometry.location.lng() });

    // Auto-detect city and neighborhood from starting address
    if (field === 'startingPoint' && place.address_components) {
      const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');

      const cityComp = place.address_components.find(c =>
        c.types.includes('locality') || c.types.includes('sublocality')
      );
      if (cityComp) {
        handleInputChange('city', cityComp.long_name);
        handleInputChange('citySlug', slugify(cityComp.long_name));
      }

      // Neighborhood: check neighborhood, sublocality_level_1, sublocality in order
      const nhComp = place.address_components.find(c => c.types.includes('neighborhood'))
        || place.address_components.find(c => c.types.includes('sublocality_level_1'))
        || place.address_components.find(c => c.types.includes('sublocality'));
      if (nhComp) {
        handleInputChange('neighborhood', nhComp.long_name);
        handleInputChange('neighborhoodSlug', slugify(nhComp.long_name));
      }
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    practicalSummary: '',
    startingPoint: '',
    endingPoint: '',
    duration: '',
    distance: '',
    difficulty: '',
    categories: [],
    tags: [],
    city: '',
    citySlug: '',
    neighborhood: '',
    neighborhoodSlug: '',
    transitAccess: '',
    bestTimes: '',
    accessibility: {
      strollerFriendly: false,
      wheelchairAccessible: false,
      parkingAvailable: false,
      restrooms: false,
      shadeCoverage: ''
    }
  });
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedGearIds, setSelectedGearIds] = useState([]);
  const [gearCatalog, setGearCatalog] = useState([]);
  const [gearSearch, setGearSearch] = useState('');
  const [selectedHighlights, setSelectedHighlights] = useState([]);

  // Fetch gear catalog
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gear`);
        if (res.ok) {
          const data = await res.json();
          setGearCatalog(data.items || []);
        }
      } catch (e) {
        console.error('Failed to fetch gear catalog:', e);
      }
    })();
  }, []);

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    setUploadingPhotos(true);
    for (const file of files) {
      if (!file.type.startsWith('image/')) continue;
      try {
        const { url } = await uploadFile(file, 'submissions');
        setPhotos(prev => [...prev, { id: Date.now() + Math.random(), url }]);
      } catch (err) {
        console.error('Photo upload failed:', file.name, err);
        toast({ title: 'Upload failed', description: `Could not upload ${file.name}`, variant: 'destructive' });
      }
    }
    setUploadingPhotos(false);
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(photo => photo.id !== photoId));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated || !auth.currentUser) {
      toast({ title: 'Sign in required', description: 'Please sign in to submit a walk.', variant: 'destructive' });
      return;
    }

    if (!startingCoords || !endingCoords) {
      toast({ title: 'Select addresses from the dropdown', description: 'Use the autocomplete suggestions for Starting Point and Ending Point so we can get the coordinates.', variant: 'destructive' });
      return;
    }

    setIsSubmitting(true);

    try {
      const durationMatch = formData.duration.match(/(\d+)/);
      const distanceMatch = formData.distance.match(/(\d+\.?\d*)/);
      const durationMinutes = durationMatch ? parseInt(durationMatch[1]) : 60;
      const distanceMeters = distanceMatch ? parseFloat(distanceMatch[1]) * 1609.34 : 1000;

      const imageUrls = photos.map(p => p.url);

      const submissionData = {
        title: formData.title,
        description: formData.description,
        practical_summary: formData.practicalSummary,
        starting_point: formData.startingPoint,
        ending_point: formData.endingPoint,
        starting_coords: startingCoords,
        ending_coords: endingCoords,
        route_coordinates: [startingCoords, endingCoords],
        distance_meters: distanceMeters,
        duration_minutes: durationMinutes,
        difficulty: formData.difficulty || 'Easy',
        categories: Array.isArray(formData.categories) ? formData.categories : [],
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        country: 'us',
        city: formData.citySlug || 'los-angeles',
        neighborhood: formData.neighborhoodSlug || '',
        region: formData.city || 'Los Angeles',
        seo_tags: [],
        highlights: selectedHighlights.filter(h => h.type === 'regular').map(h => h.title),
        cultural_pois: selectedHighlights.filter(h => h.type === 'cultural').map(h => h.title),
        accessibility: formData.accessibility,
        images: imageUrls,
        main_image: imageUrls[0] || null,
        walk_type: 'community',
        visibility: 'public',
        featured: false,
        meta_title: formData.title,
        meta_description: formData.description.substring(0, 160),
        related_gear_ids: selectedGearIds,
      };

      const authToken = await auth.currentUser.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Handle different types of error responses
        let errorMessage = 'Failed to submit walk';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            // Validation errors
            errorMessage = errorData.detail.map(err => err.msg).join(', ');
          } else if (typeof errorData.detail === 'string') {
            // Simple error message
            errorMessage = errorData.detail;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Walk submitted successfully:', result);
      
      toast({
        title: "Walk Submitted Successfully!",
        description: "Thank you for sharing your walk. We'll review it and add it to our directory soon.",
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        practicalSummary: '',
        startingPoint: '',
        endingPoint: '',
        duration: '',
        distance: '',
        difficulty: '',
        categories: [],
        tags: [],
        city: '',
        citySlug: '',
        neighborhood: '',
        neighborhoodSlug: '',
        transitAccess: '',
        bestTimes: '',
        accessibility: {
          strollerFriendly: false,
          wheelchairAccessible: false,
          parkingAvailable: false,
          restrooms: false,
          shadeCoverage: ''
        }
      });
      setPhotos([]);
      setSelectedGearIds([]);
      setSelectedHighlights([]);
      
    } catch (error) {
      console.error('Error submitting walk:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "There was an error submitting your walk. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-16 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
            <MapPin className="w-8 h-8 text-black" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Share Your Walk</h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Help fellow explorers discover amazing walks by sharing your favorite routes and hidden gems
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Information */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-amber-600">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="title">Walk Title *</Label>
                <Input
                  id="title"
                  placeholder="e.g., Silver Lake Reservoir & Murals"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Write a compelling narrative about this walk. What makes it special? What will people discover?"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="practicalSummary">Practical Summary *</Label>
                <Textarea
                  id="practicalSummary"
                  placeholder="A brief, practical overview of the walk (distance, key features, ideal for...)"
                  value={formData.practicalSummary}
                  onChange={(e) => handleInputChange('practicalSummary', e.target.value)}
                  rows={2}
                  required
                />
              </div>
            </CardContent>
          </Card>

          {/* Location & Route Details */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-amber-600">Location & Route Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startingPoint">Starting Point *</Label>
                  {mapsLoaded ? (
                    <Autocomplete
                      onLoad={(ac) => { ac.setFields(['geometry', 'formatted_address', 'address_components', 'name']); startingAutocompleteRef.current = ac; }}
                      onPlaceChanged={() => handlePlaceSelect(startingAutocompleteRef, 'startingPoint', setStartingCoords)}
                      options={{ componentRestrictions: { country: 'us' } }}
                    >
                      <Input
                        id="startingPoint"
                        placeholder="e.g., Silver Lake Reservoir Dog Park"
                        defaultValue={formData.startingPoint}
                        onChange={(e) => { handleInputChange('startingPoint', e.target.value); setStartingCoords(null); }}
                        required
                      />
                    </Autocomplete>
                  ) : (
                    <Input
                      id="startingPoint"
                      placeholder="e.g., Silver Lake Reservoir Dog Park"
                      value={formData.startingPoint}
                      onChange={(e) => handleInputChange('startingPoint', e.target.value)}
                      required
                    />
                  )}
                  {startingCoords && (
                    <p className="text-xs text-green-600 mt-1">📍 {startingCoords.lat.toFixed(5)}, {startingCoords.lng.toFixed(5)}</p>
                  )}
                  {/* Auto-detected neighborhood */}
                  {formData.neighborhood && (
                    <p className="text-xs text-gray-500 mt-1">Neighborhood: <strong>{formData.neighborhood}</strong></p>
                  )}
                </div>
                <div>
                  <Label htmlFor="endingPoint">Ending Point *</Label>
                  {mapsLoaded ? (
                    <Autocomplete
                      onLoad={(ac) => { ac.setFields(['geometry', 'formatted_address', 'address_components', 'name']); endingAutocompleteRef.current = ac; }}
                      onPlaceChanged={() => handlePlaceSelect(endingAutocompleteRef, 'endingPoint', setEndingCoords)}
                      options={{ componentRestrictions: { country: 'us' } }}
                    >
                      <Input
                        id="endingPoint"
                        placeholder="e.g., Silver Lake Reservoir Dog Park"
                        defaultValue={formData.endingPoint}
                        onChange={(e) => { handleInputChange('endingPoint', e.target.value); setEndingCoords(null); }}
                        required
                      />
                    </Autocomplete>
                  ) : (
                    <Input
                      id="endingPoint"
                      placeholder="e.g., Silver Lake Reservoir Dog Park"
                      value={formData.endingPoint}
                      onChange={(e) => handleInputChange('endingPoint', e.target.value)}
                      required
                    />
                  )}
                  {endingCoords && (
                    <p className="text-xs text-green-600 mt-1">📍 {endingCoords.lat.toFixed(5)}, {endingCoords.lng.toFixed(5)}</p>
                  )}
                </div>
              </div>

              {/* Neighborhood */}
              <div>
                <Label htmlFor="neighborhoodManual">Neighborhood</Label>
                <Input
                  id="neighborhoodManual"
                  placeholder="e.g. Silver Lake, Echo Park, DTLA"
                  value={formData.neighborhood}
                  onChange={(e) => {
                    const name = e.target.value;
                    handleInputChange('neighborhood', name);
                    handleInputChange('neighborhoodSlug', name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, ''));
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">Helps your walk appear in neighborhood searches</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="duration">Duration *</Label>
                  <Input
                    id="duration"
                    placeholder="e.g., 45-60 minutes"
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="distance">Distance *</Label>
                  <Input
                    id="distance"
                    placeholder="e.g., 2.2 miles"
                    value={formData.distance}
                    onChange={(e) => handleInputChange('distance', e.target.value)}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="difficulty">Difficulty *</Label>
                  <Select value={formData.difficulty} onValueChange={(value) => handleInputChange('difficulty', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                    <SelectContent>
                      {difficulties.map((difficulty) => (
                        <SelectItem key={difficulty} value={difficulty}>
                          {difficulty}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Categories *</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {SUBMIT_CATEGORIES.map(cat => {
                    const selected = formData.categories.includes(cat.slug);
                    return (
                      <button key={cat.slug} type="button"
                        onClick={() => handleInputChange('categories',
                          selected ? formData.categories.filter(c => c !== cat.slug) : [...formData.categories, cat.slug]
                        )}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                          selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                        }`}
                      >{cat.label}</button>
                    );
                  })}
                </div>
              </div>

              {/* City */}
              <div>
                <Label htmlFor="cityManual">City</Label>
                <Input
                  id="cityManual"
                  value={formData.city}
                  onChange={(e) => {
                    const name = e.target.value;
                    handleInputChange('city', name);
                    handleInputChange('citySlug', name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, ''));
                  }}
                  placeholder="e.g. Los Angeles"
                />
                {formData.city && startingCoords && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3" /> Auto-detected from address — edit if needed
                  </p>
                )}
              </div>

              {/* Tags */}
              <div>
                <Label>Tags</Label>
                <p className="text-xs text-gray-500 mb-2">Tags help walkers find your route</p>
                {TAG_GROUPS.map(({ group, tags }) => (
                  <div key={group} className="mb-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{group}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tags.map(tag => {
                        const selected = formData.tags.includes(tag);
                        return (
                          <button key={tag} type="button"
                            onClick={() => handleInputChange('tags',
                              selected ? formData.tags.filter(t => t !== tag) : [...formData.tags, tag]
                            )}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              selected ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'
                            }`}
                          >{tagLabel(tag)}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              <div>
                <Label htmlFor="transitAccess">Public Transit Access</Label>
                <Input
                  id="transitAccess"
                  placeholder="e.g., Metro Bus 2, 4, 302 to Silver Lake Blvd"
                  value={formData.transitAccess}
                  onChange={(e) => handleInputChange('transitAccess', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Photos */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-amber-600">Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="border-2 border-dashed border-amber-300 rounded-lg p-6 text-center">
                  <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Upload photos of your walk</p>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                    id="photo-upload"
                    ref={(input) => {
                      if (input) {
                        window.photoUploadInput = input;
                      }
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('photo-upload').click()}
                    disabled={uploadingPhotos}
                    className="cursor-pointer"
                  >
                    <Upload className="mr-2 h-4 w-4" />
                    {uploadingPhotos ? 'Uploading...' : 'Choose Photos'}
                  </Button>
                </div>
                
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative">
                        <img
                          src={photo.url}
                          alt="Walk photo"
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePhoto(photo.id)}
                          className="absolute top-1 right-1 h-6 w-6 p-0 bg-black/50 hover:bg-black/70 text-white"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Walk Highlights */}
          <HighlightsManager
            selectedHighlights={selectedHighlights}
            onHighlightsChange={setSelectedHighlights}
            walkCoordinates={startingCoords}
            title="Walk Highlights"
          />

          {/* Gear Section */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-amber-600">Gear Recommendations</CardTitle>
              <p className="text-sm text-gray-600">
                Select 2–5 gear items you'd recommend for this walk
              </p>
            </CardHeader>
            <CardContent>
              {/* Selected gear */}
              {selectedGearIds.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedGearIds.map(slug => {
                    const g = gearCatalog.find(i => i.slug === slug);
                    if (!g) return null;
                    return (
                      <span key={slug} className="inline-flex items-center gap-2 pl-1 pr-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                        {g.images?.[0] && <img src={g.images[0]} alt="" className="w-5 h-5 rounded-full object-contain bg-white" />}
                        {g.name}
                        <button type="button" onClick={() => setSelectedGearIds(prev => prev.filter(s => s !== slug))} className="p-0.5 rounded-full hover:bg-amber-200">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Search */}
              <Input
                placeholder="Search gear..."
                value={gearSearch}
                onChange={(e) => setGearSearch(e.target.value)}
                className="mb-3"
              />

              {/* Gear list */}
              <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                {gearCatalog
                  .filter(g => !gearSearch || g.name.toLowerCase().includes(gearSearch.toLowerCase()) || g.brand.toLowerCase().includes(gearSearch.toLowerCase()))
                  .map(g => {
                    const selected = selectedGearIds.includes(g.slug);
                    const atLimit = selectedGearIds.length >= 5 && !selected;
                    return (
                      <button
                        key={g.slug}
                        type="button"
                        disabled={atLimit}
                        onClick={() => {
                          if (selected) {
                            setSelectedGearIds(prev => prev.filter(s => s !== g.slug));
                          } else {
                            setSelectedGearIds(prev => [...prev, g.slug]);
                          }
                        }}
                        className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                          selected ? 'bg-amber-50 border-l-2 border-l-amber-500' : atLimit ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-10 h-10 bg-white rounded border flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {g.images?.[0] ? (
                            <img src={g.images[0]} alt={g.name} className="w-full h-full object-contain p-0.5" />
                          ) : (
                            <span className="text-xs text-gray-300">{g.brand}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{g.name}</p>
                          <p className="text-xs text-gray-500">{g.brand} · ${g.price}</p>
                        </div>
                        {selected && <span className="text-amber-500 text-xs font-bold flex-shrink-0">✓ Selected</span>}
                      </button>
                    );
                  })}
              </div>
              {selectedGearIds.length > 0 && (
                <p className="text-xs text-gray-400 mt-2">{selectedGearIds.length}/5 selected</p>
              )}
            </CardContent>
          </Card>

          {/* Additional Details */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader>
              <CardTitle className="text-amber-600">Additional Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="bestTimes">Best Times to Visit</Label>
                <Textarea
                  id="bestTimes"
                  placeholder="e.g., Early morning (7-9 AM) or golden hour (5-7 PM). Avoid midday heat in summer."
                  value={formData.bestTimes}
                  onChange={(e) => handleInputChange('bestTimes', e.target.value)}
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="text-base font-medium">Accessibility Features</Label>
                <div className="mt-3 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="strollerFriendly"
                      checked={formData.accessibility.strollerFriendly}
                      onCheckedChange={(checked) => handleInputChange('accessibility.strollerFriendly', checked)}
                    />
                    <Label htmlFor="strollerFriendly">Stroller Friendly</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="wheelchairAccessible"
                      checked={formData.accessibility.wheelchairAccessible}
                      onCheckedChange={(checked) => handleInputChange('accessibility.wheelchairAccessible', checked)}
                    />
                    <Label htmlFor="wheelchairAccessible">Wheelchair Accessible</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="parkingAvailable"
                      checked={formData.accessibility.parkingAvailable}
                      onCheckedChange={(checked) => handleInputChange('accessibility.parkingAvailable', checked)}
                    />
                    <Label htmlFor="parkingAvailable">Parking Available</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="restrooms"
                      checked={formData.accessibility.restrooms}
                      onCheckedChange={(checked) => handleInputChange('accessibility.restrooms', checked)}
                    />
                    <Label htmlFor="restrooms">Restrooms Available</Label>
                  </div>
                </div>
                
                <div className="mt-4">
                  <Label htmlFor="shadeCoverage">Shade Coverage</Label>
                  <Select 
                    value={formData.accessibility.shadeCoverage} 
                    onValueChange={(value) => handleInputChange('accessibility.shadeCoverage', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select shade coverage" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excellent">Excellent</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                      <SelectItem value="Moderate">Moderate</SelectItem>
                      <SelectItem value="Limited">Limited</SelectItem>
                      <SelectItem value="None">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="text-center">
            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold px-12 py-3 text-lg"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Walk'}
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              Your walk will be published immediately and discoverable by the Wanderloop community.
            </p>
          </div>
        </form>
      </section>

      {/* Floating Actions */}
      <FloatingActions />
      
      <Footer />
    </div>
  );
};

export default SubmitWalkPage;